import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'
import PageHeader from './PageHeader'
import AuriHelpModal from './AuriHelpModal'
import AuriFeedbackModal from './AuriFeedbackModal'
import Footer from './Footer'

const IdeaList = ({ onSelectIdea, onCreateIdea, onCreateIdeaChat, onLogout, refreshTrigger, onAdminFeedback }) => {
  const [ideas, setIdeas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('all')
  const [reviewTab, setReviewTab] = useState('all')
  const [allOpenTasks, setAllOpenTasks] = useState([])
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isOpenTasksExpanded, setIsOpenTasksExpanded] = useState(false)
  const [isPinnedIdeasExpanded, setIsPinnedIdeasExpanded] = useState(false)
  const [isReviewedIdeasExpanded, setIsReviewedIdeasExpanded] = useState(true)
  const [isIdeasInReviewExpanded, setIsIdeasInReviewExpanded] = useState(true)
  const [isAuriHelpOpen, setIsAuriHelpOpen] = useState(false)
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)

  useEffect(() => {
    fetchIdeas()
    const interval = setInterval(() => {
      fetchIdeas()
    }, 10000)
    return () => clearInterval(interval)
  }, [refreshTrigger])

  useEffect(() => {
    if (ideas.length > 0) {
      fetchAllTasks()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ideas, refreshTrigger])

  const fetchIdeas = async () => {
    try {
      const response = await apiService.getIdeas()
      const ideasData = response.ideas || response
      setIdeas(Array.isArray(ideasData) ? ideasData : [])
      setError('')
    } catch (err) {
      setError(err.message || 'Failed to load ideas')
      setIdeas([])
    } finally {
      setLoading(false)
    }
  }

  const fetchAllTasks = async () => {
    try {
      const nonArchivedIdeas = ideas.filter(idea => !idea.archived)
      
      const allTasksResults = await Promise.all(
        nonArchivedIdeas.map(async (idea) => {
          try {
            const response = await apiService.getTasks(idea.id)
            const tasks = response.tasks || []
            const openTasks = tasks.filter(task => !task.completed)
            return openTasks.map(task => ({
              ...task,
              ideaId: idea.id,
              ideaTitle: idea.what,
              ideaArchived: idea.archived
            }))
          } catch (err) {
            console.error(`Failed to fetch tasks for idea ${idea.id}:`, err)
            return []
          }
        })
      )
      const flattenedTasks = allTasksResults.flat()
      setAllOpenTasks(flattenedTasks)
    } catch (err) {
      console.error('Failed to fetch tasks:', err)
    }
  }

  const getIdeaScore = (idea) => {
    if (!idea.opportunity_assessment?.score) return 0
    return Math.round(idea.opportunity_assessment.score)
  }

  const getScoreColor = (score) => {
    if (!score || score === 0) return 'text-gray-500'
    if (score >= 7) return 'text-green-400'
    if (score >= 5) return 'text-yellow-400'
    return 'text-red-400'
  }

  const getStatusStep = (status) => {
    if (status?.startsWith('rejected')) {
      return { step: 0, label: 'Rejected' }
    }
    
    const statusSteps = {
      'idea-refinement': { step: 1, label: 'Step 1: Triage Assessment' },
      'triage': { step: 1, label: 'Step 1: Triage Assessment' },
      'opportunity-assessment': { step: 2, label: 'Step 2: Market Demand Assessment' },
      'search-terms-generated': { step: 2, label: 'Step 2: Market Demand Assessment' },
      'web-searches-completed': { step: 2, label: 'Step 2: Market Demand Assessment' },
      'content-extraction-completed': { step: 2, label: 'Step 2: Market Demand Assessment' },
      'market-demand-evaluated': { step: 3, label: 'Step 3: Market Saturation Assessment' },
      'market-saturation-evaluated': { step: 4, label: 'Step 4: USP Analysis' },
      'usp-evaluated': { step: 5, label: 'Step 5: Opportunity Assessment' },
      'opportunity-assessed': { step: 5, label: 'Step 5: Opportunity Assessment' }
    }
    return statusSteps[status] || { step: 1, label: 'Step 1: Triage Assessment' }
  }

  const getSaturationColor = (score) => {
    if (!score || score === 0) return 'text-gray-500'
    if (score < 5) return 'text-green-400'
    if (score < 7) return 'text-yellow-400'
    return 'text-red-400'
  }

  const groupedIdeas = {
    strong: ideas.filter(idea => getIdeaScore(idea) >= 7 && idea.opportunity_assessment?.score && !idea.archived),
    moderate: ideas.filter(idea => {
      const score = getIdeaScore(idea)
      return score >= 5 && score < 7 && idea.opportunity_assessment?.score && !idea.archived
    }),
    weak: ideas.filter(idea => {
      const score = getIdeaScore(idea)
      return score > 0 && score < 5 && idea.opportunity_assessment?.score && !idea.archived
    }),
    attention: ideas.filter(idea => 
      idea.status === 'idea-refinement' && !idea.archived
    ),
    inProgress: ideas.filter(idea => 
      ['triage', 'opportunity-assessment', 'search-terms-generated', 'web-searches-completed', 'content-extraction-completed', 'market-demand-evaluated', 'market-saturation-evaluated', 'usp-evaluated'].includes(idea.status) && !idea.archived
    ),
    rejected: ideas.filter(idea => 
      idea.status?.startsWith('rejected') && !idea.archived
    ),
    all: ideas.filter(idea => 
      !idea.opportunity_assessment?.score && !idea.archived
    ),
    pinned: ideas.filter(idea => idea.pinned && !idea.archived),
    archived: ideas.filter(idea => idea.archived),
  }

  const allIdeas = [...groupedIdeas.strong, ...groupedIdeas.moderate, ...groupedIdeas.weak]

  const openTasks = allOpenTasks.slice(0, 5)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-white text-xl">Loading ideas...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    )
  }

  return (
    <div className="min-h-full">
      <PageHeader
        title="Dashboard"
        onCreateIdea={onCreateIdea}
        onCreateIdeaChat={onCreateIdeaChat}
        onLogout={onLogout}
        onHelp={() => setIsAuriHelpOpen(true)}
        onFeedback={() => setIsFeedbackOpen(true)}
        transparentStats={false}
        navItems={
          <>
            <a href="#" aria-current="page" className="hidden md:block rounded-md bg-gray-900 px-3 py-2 text-sm font-display font-light tracking-tight text-white">Dashboard</a>
            {onAdminFeedback && (
              <button
                onClick={onAdminFeedback}
                className="hidden md:block rounded-md px-3 py-2 text-sm font-display font-light tracking-tight text-gray-300 hover:bg-gray-800 hover:text-white"
              >
                Admin
              </button>
            )}
          </>
        }
        stats={
          <>
            {window.innerWidth >= 768 ? (
              <>
                <span className="font-display font-light tracking-tight text-white/80">Total Ideas: <span className="font-display font-light tracking-tight text-white">{ideas.length}</span></span>
                <span className="font-display font-light tracking-tight text-white/80">Ideas Needing your attention: <span className="font-display font-light tracking-tight text-white">{groupedIdeas.attention.length}</span></span>
                <span className="font-display font-light tracking-tight text-white/80">Open Tasks: <span className="font-display font-light tracking-tight text-white">{allOpenTasks.length}</span></span>
              </>
            ) : (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors relative z-[60]"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            )}
          </>
        }
      />

      <main className="-mt-12 md:-mt-24 pb-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-black/20"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed top-40 right-4 z-50 w-64 rounded-2xl overflow-hidden bg-gradient-to-br from-gray-700/80 to-gray-700/80 shadow-none outline -outline-offset-1 outline-white/10">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none z-10"></div>
          <button
            onClick={() => {
              onCreateIdea()
              setIsMobileMenuOpen(false)
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-300 hover:bg-white/5 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="font-medium">New Idea</span>
          </button>
          
          <div className="border-t border-gray-700">
            <button
              onClick={() => {
                setIsAuriHelpOpen(true)
                setIsMobileMenuOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-300 hover:bg-white/5 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">Help</span>
            </button>
            <button
              onClick={() => {
                setIsFeedbackOpen(true)
                setIsMobileMenuOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-300 hover:bg-white/5 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              <span className="font-medium">Feedback</span>
            </button>
          </div>
          {onAdminFeedback && (
            <div className="border-t border-gray-700">
              <button
                onClick={() => {
                  onAdminFeedback()
                  setIsMobileMenuOpen(false)
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-300 hover:bg-white/5 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-medium">Admin</span>
              </button>
            </div>
          )}
          <div className="border-t border-gray-700">
            <button
              onClick={() => {
                onLogout()
                setIsMobileMenuOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-300 hover:bg-white/5 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      )}

          <h1 className="sr-only">Page title</h1>

          <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-3 lg:gap-8">
            {/* Mobile: Open Tasks and Pinned Ideas first */}
            <div className="grid grid-cols-1 gap-8 lg:hidden lg:order-2">
              <section aria-labelledby="open-tasks-title-mobile">
                <h2 id="open-tasks-title-mobile" className="sr-only">Open Tasks</h2>
                <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-purple-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-purple-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
                  
                  <button 
                    onClick={() => setIsOpenTasksExpanded(!isOpenTasksExpanded)}
                    className={`w-full font-display text-2xl md:text-3xl font-light tracking-tight text-white flex items-center justify-between gap-3 relative z-10 hover:opacity-80 transition-opacity ${isOpenTasksExpanded ? 'mb-4' : 'mb-0'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5 text-white">
                          <path d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <span>Open Tasks</span>
                      <span className="text-sm bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full">{allOpenTasks.length}</span>
                    </div>
                    <svg 
                      className={`w-6 h-6 transition-transform ${isOpenTasksExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isOpenTasksExpanded && (
                  <div className="p-0 space-y-3 relative z-10">
                    {openTasks.length > 0 ? (
                      <>
                        {openTasks.map(task => {
                          const idea = ideas.find(i => i.id === task.ideaId)
                          return (
                            <div
                              key={`${task.ideaId}-${task.id}`}
                              className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-800/70 relative"
                              onClick={() => onSelectIdea({ ...idea, openTasksTab: true, highlightTaskId: task.task_id })}
                            >
                              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                              <div className="w-4 h-4 border-2 border-purple-500 rounded flex-shrink-0"></div>
                              <div className="flex-1">
                                <div className="font-display text-lg font-light tracking-tight text-gray-100">
                                  {task.title}
                                </div>
                                <div className="text-xs text-gray-400">
                                  <span className="font-medium">{task.ideaTitle?.substring(0, 50)}{task.ideaTitle?.length > 50 ? '...' : ''}</span>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                        {allOpenTasks.length > 5 && (
                          <p className="text-xs text-gray-500 text-center mt-3 pt-3 border-t border-gray-700/50">
                            View all tasks in the Tasks tab of each idea
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-sm">No open tasks at the moment</p>
                        <p className="text-xs mt-1">Create tasks for your ideas to focus on next steps</p>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </section>

              <section aria-labelledby="pinned-ideas-title-mobile">
                <h2 id="pinned-ideas-title-mobile" className="sr-only">Pinned Ideas</h2>
                <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-teal-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-teal-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
                  
                  <button 
                    onClick={() => setIsPinnedIdeasExpanded(!isPinnedIdeasExpanded)}
                    className={`w-full font-display text-2xl md:text-3xl font-light tracking-tight text-white flex items-center justify-between gap-3 relative z-10 hover:opacity-80 transition-opacity ${isPinnedIdeasExpanded ? 'mb-4' : 'mb-0'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5 text-white">
                          <path d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <span>Pinned Ideas</span>
                      <span className="text-sm bg-teal-500/20 text-teal-300 px-2 py-1 rounded-full">{groupedIdeas.pinned.length}</span>
                    </div>
                    <svg 
                      className={`w-6 h-6 transition-transform ${isPinnedIdeasExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isPinnedIdeasExpanded && (
                  <div className="p-0 space-y-3 relative z-10">
                    {groupedIdeas.pinned.length > 0 ? (
                      groupedIdeas.pinned.map(idea => (
                        <div 
                          key={idea.id}
                          className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-800/70 relative"
                          onClick={() => onSelectIdea(idea)}
                        >
                          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-teal-500 flex-shrink-0 -ml-1">
                            <path fillRule="evenodd" d="M10 2c-1.716 0-3.408.106-5.07.31C3.806 2.45 3 3.414 3 4.517V17.25a.75.75 0 0 0 1.075.676L10 15.082l5.925 2.844A.75.75 0 0 0 17 17.25V4.517c0-1.103-.806-2.068-1.93-2.207A41.403 41.403 0 0 0 10 2Z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <div className="font-display text-lg font-light tracking-tight text-gray-100">
                              {idea.title || (idea.what?.substring(0, 80) + '...')  || 'Untitled idea'}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-sm">No pinned ideas yet</p>
                        <p className="text-xs mt-1">Pin important ideas to keep them easily accessible</p>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </section>
            </div>

            <div className="grid grid-cols-1 gap-8 lg:col-span-2 lg:order-1">
              <section aria-labelledby="reviewed-ideas-title">
                <h2 id="reviewed-ideas-title" className="sr-only">Reviewed Ideas</h2>
                <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-green-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-green-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
                  
                  <div className="relative z-10">
                    <button 
                      onClick={() => setIsReviewedIdeasExpanded(!isReviewedIdeasExpanded)}
                      className={`w-full lg:pointer-events-none font-display text-2xl md:text-3xl font-light tracking-tight text-white flex items-center justify-between gap-3 lg:hover:opacity-100 hover:opacity-80 transition-opacity ${isReviewedIdeasExpanded ? 'mb-4' : 'mb-0'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-white">
                            <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <span>Reviewed Ideas</span>
                        <span className="lg:hidden text-sm bg-green-500/20 text-green-300 px-2 py-1 rounded-full">{allIdeas.length}</span>
                      </div>
                      <svg 
                        className={`lg:hidden w-6 h-6 transition-transform ${isReviewedIdeasExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {isReviewedIdeasExpanded && (
                    <>
                    <div className="border-b border-white/10">
                      <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                        <button 
                          onClick={() => setActiveTab('all')}
                          className={`flex border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
                            activeTab === 'all' 
                              ? 'border-white text-white'
                              : 'border-transparent text-gray-400 hover:border-white hover:text-white'
                          }`}
                        >
                          <span className="hidden md:inline">All Ideas</span><span className="md:hidden">All</span>
                          <span className={`ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block ${
                            activeTab === 'all'
                              ? 'bg-white/20 text-white'
                              : 'bg-white/10 text-gray-300'
                          }`}>
                            {allIdeas.length}
                          </span>
                        </button>
                        <button 
                          onClick={() => setActiveTab('strong')}
                          className={`flex border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
                            activeTab === 'strong' 
                              ? 'border-green-400 text-green-400'
                              : 'border-transparent text-gray-400 hover:border-green-400 hover:text-green-400'
                          }`}
                        >
                          <span className="hidden md:inline">Strong Ideas</span><span className="md:hidden">Strong</span>
                          <span className={`ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block ${
                            activeTab === 'strong'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-white/10 text-gray-300'
                          }`}>
                            {groupedIdeas.strong.length}
                          </span>
                        </button>
                        <button 
                          onClick={() => setActiveTab('moderate')}
                          className={`flex border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
                            activeTab === 'moderate' 
                              ? 'border-yellow-400 text-yellow-400'
                              : 'border-transparent text-gray-400 hover:border-yellow-400 hover:text-yellow-400'
                          }`}
                        >
                          <span className="hidden md:inline">Moderate Ideas</span><span className="md:hidden">Moderate</span>
                          <span className={`ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block ${
                            activeTab === 'moderate'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-white/10 text-gray-300'
                          }`}>
                            {groupedIdeas.moderate.length}
                          </span>
                        </button>
                        <button 
                          onClick={() => setActiveTab('weak')}
                          className={`flex border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
                            activeTab === 'weak' 
                              ? 'border-red-400 text-red-400'
                              : 'border-transparent text-gray-400 hover:border-red-400 hover:text-red-400'
                          }`}
                        >
                          <span className="hidden md:inline">Weak Ideas</span><span className="md:hidden">Weak</span>
                          <span className={`ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block ${
                            activeTab === 'weak'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-white/10 text-gray-300'
                          }`}>
                            {groupedIdeas.weak.length}
                          </span>
                        </button>
                      </nav>
                    </div>

                    {(activeTab === 'all' ? allIdeas : groupedIdeas[activeTab]).length > 0 ? (
                      <ul role="list" className="grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-2 xl:gap-x-8 mt-6">
                        {(activeTab === 'all' ? allIdeas : groupedIdeas[activeTab]).map(idea => {
                          const score = getIdeaScore(idea)
                          return (
                            <li 
                              key={idea.id} 
                              className="overflow-hidden rounded-xl outline outline-1 -outline-offset-1 outline-white/10 shadow-xl relative cursor-pointer hover:shadow-2xl transition-shadow"
                              onClick={() => onSelectIdea(idea)}
                            >
                              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent pointer-events-none"></div>
                              <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-white/15 via-transparent to-transparent rounded-tl-xl pointer-events-none"></div>
                              <div className="flex items-center gap-x-4 bg-gray-800/50 p-6 relative z-10">
                                <div className="font-display text-lg font-light tracking-tight text-white flex-1">
                                  {idea.title || (idea.what?.substring(0, 80) + '...')  || 'Untitled idea'}
                                </div>
                                <div className="text-center">
                                  <div className={`text-2xl font-bold ${getScoreColor(score)}`}>
                                    {score}<span className="text-lg text-gray-400">/10</span>
                                  </div>
                                </div>
                              </div>
                              <div className="px-6 py-4 bg-transparent relative z-10">
                                <div className="grid grid-cols-3 gap-2 text-center">
                                  <div className="py-4 px-2 rounded border bg-gray-800 border-gray-700 flex flex-col justify-center">
                                    <div className="font-medium text-sm mb-1 text-gray-300">Market Demand</div>
                                    <div className={`font-bold text-lg ${getScoreColor(idea.opportunity_assessment?.market_demand_score || 0)}`}>
                                      {Math.round(idea.opportunity_assessment?.market_demand_score || 0)}/10
                                    </div>
                                  </div>
                                  <div className="py-4 px-2 rounded border bg-gray-800 border-gray-700 flex flex-col justify-center">
                                    <div className="font-medium text-sm mb-1 text-gray-300">Market Saturation</div>
                                    <div className={`font-bold text-lg ${getSaturationColor(idea.opportunity_assessment?.market_saturation_score || 0)}`}>
                                      {Math.round(idea.opportunity_assessment?.market_saturation_score || 0)}/10
                                    </div>
                                  </div>
                                  <div className="py-4 px-2 rounded border bg-gray-800 border-gray-700 flex flex-col justify-center">
                                    <div className="font-medium text-sm mb-1 text-gray-300">USP Strength</div>
                                    <div className={`font-bold text-lg ${getScoreColor(idea.opportunity_assessment?.usp_score || 0)}`}>
                                      {Math.round(idea.opportunity_assessment?.usp_score || 0)}/10
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                    ) : (
                      <div className="text-center py-12 mt-6">
                        <div className="w-12 h-12 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h3 className="text-sm font-medium text-white mb-2">
                          No {activeTab === 'all' ? '' : activeTab + ' '}ideas yet
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {activeTab === 'all' 
                            ? 'Complete ideas will appear here once they finish the opportunity assessment stage'
                            : `Ideas with ${activeTab} opportunity scores will appear in this category`
                          }
                        </p>
                      </div>
                    )}
                    </>
                    )}
                  </div>
                </div>
              </section>

              <section aria-labelledby="ideas-in-review-title">
                <h2 id="ideas-in-review-title" className="sr-only">Ideas in Review</h2>
                <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-blue-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-blue-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
                  
                  <div className="relative z-10">
                    <button 
                      onClick={() => setIsIdeasInReviewExpanded(!isIdeasInReviewExpanded)}
                      className={`w-full lg:pointer-events-none font-display text-2xl md:text-3xl font-light tracking-tight text-white flex items-center justify-between gap-3 lg:hover:opacity-100 hover:opacity-80 transition-opacity ${isIdeasInReviewExpanded ? 'mb-4' : 'mb-0'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6 text-white">
                            <path d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <span>Ideas in Review</span>
                        <span className="lg:hidden text-sm bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">{groupedIdeas.all.length}</span>
                      </div>
                      <svg className={`lg:hidden w-6 h-6 transition-transform ${isIdeasInReviewExpanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {isIdeasInReviewExpanded && (
                    <>
                    <div className="border-b border-white/10">
                      <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                        <button 
                          onClick={() => setReviewTab('all')}
                          className={`flex border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
                            reviewTab === 'all' 
                              ? 'border-white text-white'
                              : 'border-transparent text-gray-400 hover:border-white/20 hover:text-white'
                          }`}
                        >
                          <span className="hidden md:inline">All Ideas</span><span className="md:hidden">All</span>
                          <span className={`ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block ${
                            reviewTab === 'all'
                              ? 'bg-white/20 text-white'
                              : 'bg-white/10 text-gray-300'
                          }`}>
                            {groupedIdeas.all.length}
                          </span>
                        </button>
                        <button 
                          onClick={() => setReviewTab('attention')}
                          className={`flex border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
                            reviewTab === 'attention' 
                              ? 'border-orange-400 text-orange-400'
                              : 'border-transparent text-gray-400 hover:border-orange-400 hover:text-orange-400'
                          }`}
                        >
                          <span className="hidden md:inline">Need Your Attention</span><span className="md:hidden">Need Attention</span>
                          <span className={`ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block ${
                            reviewTab === 'attention'
                              ? 'bg-orange-500/20 text-orange-400'
                              : 'bg-white/10 text-gray-300'
                          }`}>
                            {groupedIdeas.attention.length}
                          </span>
                        </button>
                        <button 
                          onClick={() => setReviewTab('inProgress')}
                          className={`flex border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
                            reviewTab === 'inProgress' 
                              ? 'border-blue-400 text-blue-400'
                              : 'border-transparent text-gray-400 hover:border-blue-400 hover:text-blue-400'
                          }`}
                        >
                          In Progress
                          <span className={`ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block ${
                            reviewTab === 'inProgress'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-white/10 text-gray-300'
                          }`}>
                            {groupedIdeas.inProgress.length}
                          </span>
                        </button>
                        <button 
                          onClick={() => setReviewTab('rejected')}
                          className={`flex border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
                            reviewTab === 'rejected' 
                              ? 'border-red-400 text-red-400'
                              : 'border-transparent text-gray-400 hover:border-red-400 hover:text-red-400'
                          }`}
                        >
                          Rejected
                          <span className={`ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block ${
                            reviewTab === 'rejected'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-white/10 text-gray-300'
                          }`}>
                            {groupedIdeas.rejected.length}
                          </span>
                        </button>
                      </nav>
                    </div>

                    {groupedIdeas[reviewTab].length === 0 ? (
                      <div className="mt-6 text-center py-12">
                        <div className="w-12 h-12 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h3 className="text-sm font-medium text-white mb-2">No ideas in this category</h3>
                        <p className="text-sm text-gray-400">
                          {reviewTab === 'all' && "You don't have any ideas currently being reviewed."}
                          {reviewTab === 'attention' && "You don't have any ideas that need your attention right now."}
                          {reviewTab === 'inProgress' && "You don't have any ideas currently in progress."}
                          {reviewTab === 'rejected' && "You don't have any rejected ideas."}
                        </p>
                      </div>
                    ) : (
                      <ul role="list" className="grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-2 xl:gap-x-8 mt-6">
                        {groupedIdeas[reviewTab].map(idea => (
                          <li 
                            key={idea.id} 
                            className="overflow-hidden rounded-xl outline outline-1 -outline-offset-1 outline-white/10 shadow-xl relative cursor-pointer hover:shadow-2xl transition-shadow"
                            onClick={() => onSelectIdea(idea)}
                          >
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent pointer-events-none"></div>
                            <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-white/15 via-transparent to-transparent rounded-tl-xl pointer-events-none"></div>
                            <div className="bg-gray-800/50 p-6 relative z-10 flex flex-col h-40">
                              <div className="flex items-start gap-x-4 flex-1">
                                <div className="flex-1">
                                  <div className="font-display text-lg font-light tracking-tight text-white">
                                    {idea.title || (idea.what?.substring(0, 80) + '...')  || 'Untitled idea'}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                {idea.status !== 'idea-refinement' && !idea.status?.startsWith('rejected') && (
                                  <span className="inline-flex items-center rounded-md bg-blue-900/30 px-2 py-1 text-xs font-medium text-blue-300 ring-1 ring-inset ring-blue-400/30">
                                    {getStatusStep(idea.status).label}
                                  </span>
                                )}
                                {idea.status === 'idea-refinement' && (
                                  <div className="flex flex-col gap-2">
                                    <span className="inline-flex items-center rounded-md bg-orange-900/30 px-2 py-1 text-xs font-medium text-orange-300 ring-1 ring-inset ring-orange-400/30">
                                      Needs Your Attention
                                    </span>
                                  </div>
                                )}
                                {idea.status?.startsWith('rejected') && (
                                  <span className="inline-flex items-center rounded-md bg-red-900/30 px-2 py-1 text-xs font-medium text-red-300 ring-1 ring-inset ring-red-400/30">
                                    Rejected
                                  </span>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    </>
                    )}
                  </div>
                </div>
              </section>
            </div>

            <div className="hidden lg:grid grid-cols-1 gap-8 lg:order-3">
              <section aria-labelledby="open-tasks-title">
                <h2 id="open-tasks-title" className="sr-only">Open Tasks</h2>
                <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-purple-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-purple-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
                  
                  <h1 className="font-display text-2xl font-light tracking-tight text-white mb-4 flex items-center gap-3 relative z-10">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5 text-white">
                        <path d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    Open Tasks
                  </h1>
                  
                  <div className="p-0 space-y-3 relative z-10">
                    {openTasks.length > 0 ? (
                      <>
                        {openTasks.map(task => {
                          const idea = ideas.find(i => i.id === task.ideaId)
                          return (
                            <div
                              key={`${task.ideaId}-${task.id}`}
                              className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-800/70 relative"
                              onClick={() => onSelectIdea({ ...idea, openTasksTab: true, highlightTaskId: task.task_id })}
                            >
                              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                              <div className="w-4 h-4 border-2 border-purple-500 rounded flex-shrink-0"></div>
                              <div className="flex-1">
                                <div className="font-display text-sm font-light tracking-tight text-gray-100">
                                  {task.title}
                                </div>
                                <div className="text-xs text-gray-400">
                                  <span className="font-medium">{task.ideaTitle?.substring(0, 50)}{task.ideaTitle?.length > 50 ? '...' : ''}</span>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                        {allOpenTasks.length > 5 && (
                          <p className="text-xs text-gray-500 text-center mt-3 pt-3 border-t border-gray-700/50">
                            View all tasks in the Tasks tab of each idea
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-sm">No open tasks at the moment</p>
                        <p className="text-xs mt-1">Create tasks for your ideas to focus on next steps</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section aria-labelledby="pinned-ideas-title">
                <h2 id="pinned-ideas-title" className="sr-only">Pinned Ideas</h2>
                <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-teal-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-teal-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
                  
                  <h1 className="font-display text-2xl font-light tracking-tight text-white mb-4 flex items-center gap-3 relative z-10">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5 text-white">
                        <path d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    Pinned Ideas
                  </h1>
                  
                  <div className="p-0 space-y-3 relative z-10">
                    {groupedIdeas.pinned.length > 0 ? (
                      groupedIdeas.pinned.map(idea => (
                        <div 
                          key={idea.id}
                          className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-800/70 relative"
                          onClick={() => onSelectIdea(idea)}
                        >
                          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-teal-500 flex-shrink-0 -ml-1">
                            <path fillRule="evenodd" d="M10 2c-1.716 0-3.408.106-5.07.31C3.806 2.45 3 3.414 3 4.517V17.25a.75.75 0 0 0 1.075.676L10 15.082l5.925 2.844A.75.75 0 0 0 17 17.25V4.517c0-1.103-.806-2.068-1.93-2.207A41.403 41.403 0 0 0 10 2Z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <div className="font-display text-sm font-light tracking-tight text-gray-100">
                              {idea.title || (idea.what?.substring(0, 80) + '...')  || 'Untitled idea'}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-sm">No pinned ideas yet</p>
                        <p className="text-xs mt-1">Pin important ideas to keep them easily accessible</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section aria-labelledby="archived-ideas-title">
                <h2 id="archived-ideas-title" className="sr-only">Archived Ideas</h2>
                <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                  <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                  <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-white/10 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
                  
                  <h1 className="font-display text-xl md:text-2xl font-light tracking-tight text-white mb-4 flex items-center gap-3 relative z-10">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center shadow-lg">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-5 text-white">
                        <path d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m6 4.125 2.25 2.25m0 0 2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    Archived Ideas
                  </h1>
                  
                  <div className="p-0 space-y-3 relative z-10">
                    {groupedIdeas.archived.length > 0 ? (
                      groupedIdeas.archived.map(idea => (
                        <div 
                          key={idea.id}
                          className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50 cursor-pointer hover:bg-gray-800/70 relative"
                          onClick={() => onSelectIdea(idea)}
                        >
                          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-400 flex-shrink-0 -ml-1">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                          </svg>
                          <div className="flex-1">
                            <div className="font-display text-sm font-light tracking-tight text-gray-400">
                              {idea.title || (idea.what?.substring(0, 80) + '...')  || 'Untitled idea'}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-sm">No archived ideas</p>
                        <p className="text-xs mt-1">Archive ideas you want to set aside for later</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <AuriHelpModal
        isOpen={isAuriHelpOpen}
        onClose={() => setIsAuriHelpOpen(false)}
        onCreateIdea={onCreateIdea}
      />

      <AuriFeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />

      <Footer variant="authenticated" />
    </div>
  )
}

IdeaList.propTypes = {
  onSelectIdea: PropTypes.func.isRequired,
  onCreateIdea: PropTypes.func.isRequired,
  onCreateIdeaChat: PropTypes.func,
  onLogout: PropTypes.func,
  refreshTrigger: PropTypes.number,
  onAdminFeedback: PropTypes.func,
}

export default IdeaList
